import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App, Layout } from 'antd';
import zhCN from 'antd/locale/zh_CN';

// 导入上下文
import { AuthProvider } from './contexts/AuthContext';

// 导入组件
import AppLayout from './components/Layout/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login/index';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import UserManagement from './pages/UserManagement';
import FileList from './pages/PDFExtract/FileList';
import Editor from './pages/PDFExtract/Editor';
import MatchList from './pages/Match/MatchList';
import CreateMatch from './pages/Match/CreateMatch';
import EditMatch from './pages/Match/EditMatch';
import TaskList from './pages/VideoAudit/TaskList';
import TaskDetail from './pages/VideoAudit/TaskDetail';
import DownloadList from './pages/VideoDownload/DownloadList';
import DownloadLogs from './pages/DownloadLogs';
import KnowledgeManagement from './pages/KnowledgeManagement/KnowledgeManagement';
import QuestionList from './pages/QuestionList';
import QuestionEdit from './pages/QuestionEdit';
import QuestionTypeManage from './pages/QuestionTypeManage';
import QuestionTagManage from './pages/QuestionTagManage';
import LiveSimList from './pages/LiveSim/LiveSimList';

const { Content } = Layout;

// 添加React Router未来标志配置
// 这将解决React Router的Future Flag警告
const routerFutureConfig = {
  future: {
    v7_relativeSplatPath: true,  // 解决相对路由解析警告
    v7_startTransition: true     // 解决状态更新包装警告
  }
};

function AppRoot() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#004d40',
          colorSuccess: '#00695c',
          colorWarning: '#2e7d32',
          colorError: '#d32f2f',
          colorInfo: '#00695c',
          colorTextBase: '#000000',
          colorBgBase: '#ffffff',
          borderRadius: 4,
          wireframe: false,
        },
        components: {
          Button: {
            primaryColor: '#ffffff',
            colorPrimaryHover: '#00695c',
            defaultBorderColor: '#b2dfdb',
            defaultColor: '#000000',
            defaultBg: '#ffffff',
            defaultHoverBg: '#e0f2f1',
            primaryShadow: 'none',  // 移除主要按钮的阴影
            defaultShadow: 'none',  // 移除默认按钮的阴影
          },
          Menu: {
            itemBg: '#ffffff',
            itemSelectedBg: '#e0f2f1',
            itemHoverBg: '#b2dfdb',
            itemSelectedColor: '#000000',
            itemColor: '#000000',
          },
          Card: {
            headerBg: '#e0f2f1',
            borderColor: '#b2dfdb',
            headerColor: '#000000',
          },
          Table: {
            headerBg: '#e0f2f1',
            borderColor: '#b2dfdb',
            headerColor: '#000000',
          },
          Input: {
            borderColor: '#b2dfdb',
            hoverBorderColor: '#00695c',
            colorText: '#000000',
            activeShadow: 'none',  // 移除输入框聚焦时的阴影
          },
          Select: {
            borderColor: '#b2dfdb',
            optionSelectedBg: '#e0f2f1',
            colorText: '#000000',
          },
          Tag: {
            // 设置所有状态的背景色为白色
            colorInfoBg: '#ffffff',
            colorSuccessBg: '#ffffff',
            colorProcessingBg: '#ffffff',
            colorWarningBg: '#ffffff',
            colorErrorBg: '#ffffff',
            defaultBg: '#ffffff',
            // 保持边框颜色与各自状态一致
            colorInfoBorder: '#00695c', // 中绿色
            colorSuccessBorder: '#52c41a', // 绿色
            colorProcessingBorder: '#1677ff', // 蓝色
            colorWarningBorder: '#faad14', // 黄色
            colorErrorBorder: '#ff4d4f', // 红色
          },
        },
      }}
    >
      <App>
        <AuthProvider>
          <Router future={routerFutureConfig.future}>
            <Routes>
              {/* 登录页面 */}
              <Route path="/login" element={<Login />} />
              
              {/* 受保护的路由 */}
              <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route path="change-password" element={<ChangePassword />} />
                <Route path="users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
                <Route path="download-logs" element={<ProtectedRoute adminOnly><DownloadLogs /></ProtectedRoute>} />
                <Route path="pdf">
                  <Route path="files" element={<FileList />} />
                  <Route path="edit/:id" element={<Editor />} />
                </Route>
                <Route path="match">
                  <Route path="" element={<MatchList />} />
                  <Route path="create" element={<CreateMatch />} />
                  <Route path="edit/:id" element={<EditMatch />} />
                </Route>
                <Route path="video-audit">
                  <Route path="task-list" element={<TaskList />} />
                  <Route path="detail/:taskId" element={<TaskDetail />} />
                </Route>
                <Route path="video-download" element={<DownloadList />} />
                <Route path="live-sim" element={<LiveSimList />} />
                <Route path="knowledge" element={<KnowledgeManagement />} />
                <Route path="questions" element={<QuestionList />} />
                <Route path="questions/create" element={<QuestionEdit />} />
                <Route path="questions/edit/:id" element={<QuestionEdit />} />
                <Route path="questions/types" element={<QuestionTypeManage />} />
                <Route path="questions/tags" element={<QuestionTagManage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </App>
    </ConfigProvider>
  );
}

export default AppRoot;
